import { useState, useCallback, useRef } from 'react';
import { useRetry, retryConditions } from './useRetry';
import { useFinancialErrorHandler, FinancialError, FinancialErrorType } from '../utils/financial-error-handling';
import apiClient from '../utils/api-client';

interface FinancialApiState {
  loading: boolean;
  error: FinancialError | null;
  data: any;
  retryCount: number;
  canRetry: boolean;
  nextRetryIn: number;
}

interface FinancialApiOptions {
  maxRetries?: number;
  baseDelay?: number;
  enableAutoRetry?: boolean;
  onSuccess?: (data: any) => void;
  onError?: (error: FinancialError) => void;
  onRetry?: (attempt: number, error: FinancialError) => void;
}

interface UseFinancialApiReturn {
  state: FinancialApiState;
  execute: (endpoint: string, options?: { method?: string; data?: any }) => Promise<any>;
  retry: () => Promise<any>;
  reset: () => void;
  clearError: () => void;
}

/**
 * Hook for making financial API calls with comprehensive error handling and retry logic
 */
export const useFinancialApiWithRetry = (options: FinancialApiOptions = {}): UseFinancialApiReturn => {
  const {
    maxRetries = 3,
    baseDelay = 2000,
    enableAutoRetry = true,
    onSuccess,
    onError,
    onRetry
  } = options;

  const { handleError, shouldRetry: shouldAutoRetry, getRetryDelay } = useFinancialErrorHandler();
  const lastRequestRef = useRef<{ endpoint: string; options?: any } | null>(null);

  const [state, setState] = useState<FinancialApiState>({
    loading: false,
    error: null,
    data: null,
    retryCount: 0,
    canRetry: false,
    nextRetryIn: 0
  });

  // Custom retry condition for financial operations
  const financialRetryCondition = useCallback((error: any) => {
    const financialError = handleError(error);
    return shouldAutoRetry(financialError, state.retryCount);
  }, [handleError, shouldAutoRetry, state.retryCount]);

  const { execute: executeWithRetry, retry: retryExecution, reset: resetRetry } = useRetry(
    async (endpoint: string, requestOptions?: { method?: string; data?: any }) => {
      const { method = 'GET', data } = requestOptions || {};
      
      setState(prev => ({ 
        ...prev, 
        loading: true, 
        error: null,
        canRetry: false,
        nextRetryIn: 0
      }));

      try {
        let response;
        switch (method.toUpperCase()) {
          case 'GET':
            response = await apiClient.get(endpoint);
            break;
          case 'POST':
            response = await apiClient.post(endpoint, data);
            break;
          case 'PUT':
            response = await apiClient.put(endpoint, data);
            break;
          case 'DELETE':
            response = await apiClient.delete(endpoint);
            break;
          case 'PATCH':
            response = await apiClient.patch(endpoint, data);
            break;
          default:
            throw new Error(`Unsupported HTTP method: ${method}`);
        }

        const responseData = response.data || response;
        
        setState(prev => ({
          ...prev,
          loading: false,
          data: responseData,
          error: null,
          retryCount: 0,
          canRetry: false,
          nextRetryIn: 0
        }));

        onSuccess?.(responseData);
        return responseData;
      } catch (error) {
        const financialError = handleError(error, { 
          endpoint, 
          method, 
          timestamp: new Date().toISOString() 
        });

        setState(prev => ({
          ...prev,
          loading: false,
          error: financialError,
          retryCount: prev.retryCount + 1,
          canRetry: !!(financialError.retryable && prev.retryCount < maxRetries - 1),
          nextRetryIn: 0
        }));

        onError?.(financialError);
        throw error;
      }
    },
    {
      maxAttempts: maxRetries,
      baseDelay,
      retryCondition: enableAutoRetry ? financialRetryCondition : retryConditions.never,
      onRetry: (attempt, error) => {
        const financialError = handleError(error);
        setState(prev => ({
          ...prev,
          retryCount: attempt,
          nextRetryIn: Math.ceil(getRetryDelay(financialError, attempt - 1) / 1000)
        }));
        onRetry?.(attempt, financialError);
      },
      onMaxAttemptsReached: (error) => {
        const financialError = handleError(error);
        setState(prev => ({
          ...prev,
          canRetry: false,
          nextRetryIn: 0
        }));
      }
    }
  );

  const execute = useCallback(async (endpoint: string, requestOptions?: { method?: string; data?: any }) => {
    lastRequestRef.current = { endpoint, options: requestOptions };
    return executeWithRetry(endpoint, requestOptions);
  }, [executeWithRetry]);

  const retry = useCallback(async () => {
    if (!lastRequestRef.current) {
      throw new Error('No previous request to retry');
    }
    
    const { endpoint, options } = lastRequestRef.current;
    return retryExecution();
  }, [retryExecution]);

  const reset = useCallback(() => {
    resetRetry();
    setState({
      loading: false,
      error: null,
      data: null,
      retryCount: 0,
      canRetry: false,
      nextRetryIn: 0
    });
    lastRequestRef.current = null;
  }, [resetRetry]);

  const clearError = useCallback(() => {
    setState(prev => ({
      ...prev,
      error: null,
      canRetry: !!(prev.error?.retryable && prev.retryCount < maxRetries)
    }));
  }, [maxRetries]);

  return {
    state,
    execute,
    retry,
    reset,
    clearError
  };
};

/**
 * Specialized hooks for common financial operations
 */

export const useFinancialDashboardData = (options?: FinancialApiOptions) => {
  const [state, setState] = useState<FinancialApiState>({
    loading: false,
    error: null,
    data: null,
    retryCount: 0,
    canRetry: false,
    nextRetryIn: 0
  });

  const fetchDashboardData = useCallback(async (params: {
    propertyIds: string[];
    startDate: string;
    endDate: string;
    groupBy?: string;
  }) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const queryParams = new URLSearchParams({
        propertyIds: params.propertyIds.join(','),
        startDate: params.startDate,
        endDate: params.endDate,
        ...(params.groupBy && { groupBy: params.groupBy })
      });

      const response = await apiClient.get(`/financial/dashboard?${queryParams}`);
      // Handle nested response structure: response.data.data contains the actual dashboard data
      const data = (response.data as any)?.data || response.data || response;
      
      setState(prev => ({ ...prev, loading: false, data, error: null }));
      options?.onSuccess?.(data);
      
      return data;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch dashboard data';
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: { 
          type: FinancialErrorType.NETWORK_ERROR, 
          message: errorMessage, 
          severity: 'high' 
        } 
      }));
      options?.onError?.({ type: FinancialErrorType.NETWORK_ERROR, message: errorMessage, severity: 'high' });
      throw error;
    }
  }, [options]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    state,
    fetchDashboardData,
    clearError,
    retry: () => {}, // No retry functionality
  };
};

export const useFinancialMetrics = (options?: FinancialApiOptions) => {
  const api = useFinancialApiWithRetry({
    maxRetries: 2,
    enableAutoRetry: true,
    ...options
  });

  const fetchMetrics = useCallback(async (params: {
    propertyIds: string[];
    startDate: string;
    endDate: string;
    comparisonPeriod?: { startDate: string; endDate: string };
  }) => {
    const queryParams = new URLSearchParams({
      propertyIds: params.propertyIds.join(','),
      startDate: params.startDate,
      endDate: params.endDate,
      ...(params.comparisonPeriod && {
        comparisonStartDate: params.comparisonPeriod.startDate,
        comparisonEndDate: params.comparisonPeriod.endDate
      })
    });

    return api.execute(`/financial/metrics?${queryParams}`);
  }, [api]);

  return {
    ...api,
    fetchMetrics
  };
};

export const useFinancialExport = (options?: FinancialApiOptions) => {
  const api = useFinancialApiWithRetry({
    maxRetries: 2,
    baseDelay: 5000, // Longer delay for export operations
    enableAutoRetry: false, // Manual retry for exports
    ...options
  });

  const generateExport = useCallback(async (params: {
    format: 'csv' | 'excel' | 'pdf';
    propertyIds: string[];
    startDate: string;
    endDate: string;
    sections?: string[];
    includeCharts?: boolean;
  }) => {
    return api.execute('/financial/export', {
      method: 'POST',
      data: params
    });
  }, [api]);

  const downloadExport = useCallback(async (exportId: string) => {
    return api.execute(`/financial/export/${exportId}/download`);
  }, [api]);

  return {
    ...api,
    generateExport,
    downloadExport
  };
};

export const useFinancialReports = (options?: FinancialApiOptions) => {
  const api = useFinancialApiWithRetry({
    maxRetries: 2,
    baseDelay: 10000, // Even longer delay for report generation
    enableAutoRetry: false, // Manual retry for reports
    ...options
  });

  const generateReport = useCallback(async (params: {
    template: string;
    propertyIds: string[];
    startDate: string;
    endDate: string;
    deliveryMethod: 'download' | 'email';
    emailAddress?: string;
    sections: string[];
  }) => {
    return api.execute('/reports/generate', {
      method: 'POST',
      data: params
    });
  }, [api]);

  const getReportStatus = useCallback(async (reportId: string) => {
    return api.execute(`/reports/${reportId}`);
  }, [api]);

  const downloadReport = useCallback(async (reportId: string) => {
    return api.execute(`/reports/${reportId}/download`);
  }, [api]);

  return {
    ...api,
    generateReport,
    getReportStatus,
    downloadReport
  };
};

export const useFinancialPreferences = (options?: FinancialApiOptions) => {
  const api = useFinancialApiWithRetry({
    maxRetries: 2,
    enableAutoRetry: true,
    ...options
  });

  const getPreferences = useCallback(async () => {
    return api.execute('/financial/preferences');
  }, [api]);

  const updatePreferences = useCallback(async (preferences: any) => {
    return api.execute('/financial/preferences', {
      method: 'PUT',
      data: preferences
    });
  }, [api]);

  return {
    ...api,
    getPreferences,
    updatePreferences
  };
};

export default useFinancialApiWithRetry;